import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import styles from './MyProfileMain.module.css';
import axios from 'axios';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserContext } from '../../utils/UserContext';
import { getCroppedImg } from '../../utils/imageUtils';

const MyProfileMain = () => {
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    extension: '',
    calendar: '',
    language: 'English (United States)',
    enableSignature: false,
    includeSignature: false,
    signature: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [userImages, setUserImages] = useState([]);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const fileInputRef = useRef(null);
  const [profileImageId, setProfileImageId] = useState(null);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null); 
  const [confirmText, setConfirmText] = useState('');
  const { user, setUser } = useContext(UserContext);
  const workspaceId = user?.workspace_id;

  // Fetch user data
  const fetchUserData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/user/user`, {
        params: { id: user.id, workspaceId }
      });
      setProfileData({
        firstName: data.firstName || '',
        lastName : data.lastName  || '',
        email    : data.email     || '',
        phone    : data.phoneNumber || ''
      });
      setProfileImageId(data.profileImage?.id || null);
      setUserImages(data.images || []);
      setProfileImage(data.profileImage?.url || null);
      setDataLoaded(true);
    } catch (err) {
      toast.error('Error loading user data');
    }
  }, [user.id, workspaceId]);

  useEffect(() => {
    if (user && workspaceId) fetchUserData();
  }, [user, workspaceId, fetchUserData]);

  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData({
      ...profileData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    const updatedPasswordData = {
      ...passwordData,
      [name]: value
    };
    setPasswordData(updatedPasswordData);
    if (
        (name === 'newPassword' || name === 'confirmPassword') &&
        updatedPasswordData.newPassword !== '' &&
        updatedPasswordData.confirmPassword !== ''
    ) {
        setPasswordMismatch(updatedPasswordData.newPassword !== updatedPasswordData.confirmPassword);
    } else {
        setPasswordMismatch(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        id: user.id,
        workspaceId,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phoneNumber: profileData.phone,
        profileImageId
      };
      await axios.put(`${API_URL}/user/user`, payload);
      const updatedUser = {
        ...user,
        email: profileData.email,
        phone_number: profileData.phone
      };
      Auth.setUser(updatedUser);
      setUser(updatedUser);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Error updating profile');
    }
  };

  const handleAskDeleteImage = (img) => {
    setImageToDelete(img);     
    setConfirmText('');       
    setShowDeleteModal(true); 
  };

  const handleDeleteImage = async () => {
    if (!imageToDelete) return;
    try {
      await axios({
        method: 'delete',                       
        url: `${API_URL}/user/user-delete-image`,
        headers: { 'Content-Type': 'application/json' },
        data: {                                 
          imageId: imageToDelete.id,
          userId:  user.id,
          workspaceId
        }
      });
      setUserImages(prev => prev.filter(img => img.id !== imageToDelete.id));
      if (profileImageId === imageToDelete.id) {
        const updatedUser = {
          ...user,
          profileImageId: null,
          profileImage:   null
        };
        Auth.setUser(updatedUser);
        setUser(updatedUser);
        setProfileImage(null);
        setProfileImageId(null);
      }
      toast.success('Image deleted');
    } catch (err) {
      toast.error('Error deleting image');
    } finally {
      setShowDeleteModal(false);
      setImageToDelete(null);
    }
  };

  const updateProfileImage = useCallback(
    async (imageId) => {
      try {
        await axios.put(`${API_URL}/user/user`, {
          id: user.id,
          workspaceId,
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          email: profileData.email.trim().toLowerCase(),
          phoneNumber: profileData.phone,
          profileImageId: imageId,
        });
        const img = userImages.find(img => img.id === imageId);
        const bustedUrl = `${img.url}?t=${Date.now()}`;
        const updatedUser = {
          ...user,
          profileImageId: imageId,
          profileImage: { url: bustedUrl }
        };
        Auth.setUser(updatedUser);
        setUser(updatedUser);
        toast.success('Profile image updated');
      } catch (err) {
        toast.error('Error updating profile picture');
      }
    },
    [user, userImages, profileData, workspaceId, setUser]
  );

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordData;
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    try {
      const payload = {
        id: user.id,
        workspaceId,
        oldPassword: currentPassword,
        newPassword
      };
      await axios.put(`${API_URL}/user/user`, payload);
      toast.success('Password updated successfully');
    } catch (error) {
      toast.error('Error updating password: ' + (error.response?.data?.message || error.message));
    } finally {
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordMismatch(false);
    }
  };

  const handleImageChange = async (e) => {
    setShowImageSelector(false);
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) {
      toast.warn('File size exceeds 2.5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setOriginalImage(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropAndUpload = async () => {
    if (!originalImage || !croppedAreaPixels) return;
    try {
      const croppedBase64 = await getCroppedImg(originalImage, croppedAreaPixels, zoom);
      setProfileImage(croppedBase64);
      setShowCropper(false);
      setShowImageSelector(false);
      const base64Data = croppedBase64.split(',')[1];
      const response = await axios.post(`${API_URL}/user/user-upload-image`, {
        userId: user.id,
        workspaceId,
        fileBuffer: base64Data,
        fileName: 'profile.jpg',
        mimeType: 'image/jpeg',
        setAsProfile: true
      });
      const imageData = response.data;
      if (imageData?.id && imageData?.url) {
        const bustedUrl = `${imageData.url}?t=${Date.now()}`;
        const updatedUser = {
          ...user,
          profileImageId: imageData.id,
          profileImage:   { url: bustedUrl }
        };
        Auth.setUser(updatedUser); 
        setUser(updatedUser);
        setProfileImageId(imageData.id);
        setProfileImage(bustedUrl);
        toast.success('Image uploaded successfully!');
      } else {
        toast.error('Unexpected server response when uploading image.');
      }
      fileInputRef.current.value = null;
    } catch (error) {
      toast.error('Error uploading image');
      fileInputRef.current.value = null;
    }
  };

  const handleUploadOriginal = async () => {
    if (!originalImage) return;
    setProfileImage(originalImage);
    setShowCropper(false);
    setShowImageSelector(false);
    const base64Data = originalImage.split(',')[1];
    try {
      const response = await axios.post(`${API_URL}/user/user-upload-image`, {
        userId: user.id,
        workspaceId,
        fileBuffer: base64Data,
        fileName: 'profile.jpg',
        mimeType: 'image/jpeg',
        setAsProfile: true
      });
      if (response.data && response.data.image && response.data.image.id) {
        setProfileImageId(response.data.image.id);
      }
      toast.success('Image uploaded successfully. Click Update Profile to save.');
      await fetchUserData();
    } catch (error) {
      toast.error('Error uploading image');
    }
  };

  const handleCameraClick = () => {
    setShowImageSelector(true);
  };

  const handleSelectExistingImage = async (img) => {
    setProfileImage(img.url);
    setProfileImageId(img.id);
    setShowImageSelector(false);
    await updateProfileImage(img.id);  
  };

  const handleUploadNewImage = () => {
    setShowImageSelector(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  return (
    <>
      {/* Image Selector Modal (overlay, outside main layout) */}
      {showImageSelector && (
        <div className={styles.cropperModal}>
          <div className={styles.cropperContent}>
            <h3 className={styles.imageSelectorHeading}>Select a profile image</h3>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              {userImages && userImages.length > 0 ? (
                userImages.map(img => (
                  <div
                    key={img.id}
                    className={styles.imageThumb}
                    onClick={() => handleSelectExistingImage(img)}
                    title="Select this image"
                    style={{ cursor: 'pointer', border: profileImageId === img.id ? '2px solid #3b82f6' : '2px solid transparent', borderRadius: '50%' }}
                  >
                    <img
                      src={img.url}
                      alt={`Profile image ${img.id}`}
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: '50%' }}
                    />
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => { e.stopPropagation(); handleAskDeleteImage(img); }}
                      title="Delete image"
                      style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <span style={{ color: '#888' }}>You have no saved images.</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className={styles.btn + ' ' + styles.btnPrimary} onClick={handleUploadNewImage}>Upload new image</button>
              <button className={styles.btn} onClick={() => setShowImageSelector(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className={styles.profileContainer}>
        {/* Cropper Modal */}
        {showCropper && (
          <div className={styles.cropperModal}>
            <div className={styles.cropperContent}>
              <div className={styles.cropperAreaWrapper}>
                <Cropper
                  image={originalImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round"
                  showGrid={false}
                  objectFit="contain"
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    },
                    mediaStyle: {
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }
                  }}
                />
              </div>
              <div className={styles.cropperZoomControl}>
                <label htmlFor="zoomRange">Zoom</label>
                <input
                  id="zoomRange"
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                />
              </div>
              <div className={styles.cropperActions}>
                <button className={styles.btn} onClick={handleCropAndUpload}>Crop and upload</button>
                <button className={styles.btn} onClick={handleUploadOriginal}>Upload without cropping</button>
                <button className={styles.btn} onClick={() => {setShowCropper(false); fileInputRef.current.value = null;}}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {/* Delete Image Modal */}
        {showDeleteModal && (
          <div className={styles.cropperModal}>
            <div className={styles.cropperContent} style={{ maxWidth: 600, textAlign: 'center' }}>
              <h3>Confirm deletion</h3>
              <p>Type <strong>DELETE</strong> to remove this image permanently.</p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE here"
                style={{ width: '100%', padding: 8, marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  className={styles.btn}
                  disabled={confirmText !== 'DELETE'}
                  onClick={handleDeleteImage}
                >
                  Delete
                </button>
                <button
                  className={styles.btn}
                  onClick={() => { setShowDeleteModal(false); setImageToDelete(null); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Main Layout */}
        <div className={styles.leftPanel}>
          <h2>Personal Data</h2>
          {/* Profile Image Section */}
          <div className={styles.profileImageContainer}>
            <div className={styles.profileImage}>
              {!showCropper && (
                profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : (
                  <div className={styles.defaultAvatar}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="8" r="5" />
                      <path d="M20 21v-2a7 7 0 0 0-14 0v2" />
                    </svg>
                  </div>
                )
              )}
              <div className={styles.cameraIcon} onClick={handleCameraClick} title="Change profile image">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                />
              </div>
            </div>
            <div className={styles.profileImageText}>
              <p>Profile Image</p>
              <p className={styles.imageSizeText}>The proposed size is 512×512 px no bigger than 2.5 MB</p>
            </div>
          </div>
          <form onSubmit={handleUpdateProfile}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName">
                  First Name <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="lastName">
                  Last Name <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleProfileChange}
                  required
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="email">
                  Email <span className={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                />
              </div>
            </div>
            <div className={styles.btnContainer}>
              <button type="submit" className={styles.btn + ' ' + styles.btnPrimary}>Update Profile</button>
            </div>
          </form>
        </div>
        <div className={styles.rightPanels}>
          <div className={`${styles.rightPanel} ${styles.passwordPanel}`}>
            <h2>Change Password</h2>
            <form onSubmit={handleUpdatePassword}>
              <div className={styles.formGroupPassword}>
                <label htmlFor="currentPassword">
                  Current Password <span className={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  placeholder="Current Password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              <div className={styles.formGroupPassword}>
                <label htmlFor="newPassword">
                  New Password <span className={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  placeholder="Password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              <div className={styles.formGroupPassword}>
                <label htmlFor="confirmPassword">
                  Confirm New Password <span className={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              {passwordMismatch && (
                <p style={{ color: 'red', marginTop: '10px', marginBottom: '10px' }}>
                  ⚠️ The passwords do not match.
                </p>
              )}
              <div className={styles.btnContainer}>
                <button type="submit" className={styles.btn + ' ' + styles.btnPrimary}>Update Password</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyProfileMain;